import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

// Theme-coloured replacement for the browser's native <select> (whose open
// list is OS-styled — blue highlight — and can't be themed). The list is
// portalled to <body> with fixed positioning so it never gets clipped by a
// modal's overflow.
//
// options: array of strings or { value, label }.
export default function ThemedSelect({ value, onChange, options = [], style = {}, disabled = false, placeholder = 'Select' }) {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  const btnRef = useRef(null);
  const listRef = useRef(null);

  const opts = options.map((o) => (typeof o === 'object' ? o : { value: o, label: o }));
  const current = opts.find((o) => o.value === value);

  const openList = useCallback(() => {
    if (disabled || !btnRef.current) return;
    setRect(btnRef.current.getBoundingClientRect());
    setOpen(true);
  }, [disabled]);

  const choose = (v) => {
    setOpen(false);
    if (v !== value && onChange) onChange(v);
    if (btnRef.current) btnRef.current.focus();
  };

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (btnRef.current && btnRef.current.contains(e.target)) return;
      if (listRef.current && listRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setOpen(false); }
    };
    const close = () => setOpen(false);
    document.addEventListener('mousedown', onDown, true);
    document.addEventListener('touchstart', onDown, true);
    document.addEventListener('keydown', onKey, true);
    window.addEventListener('resize', close);
    window.addEventListener('scroll', close, true);
    return () => {
      document.removeEventListener('mousedown', onDown, true);
      document.removeEventListener('touchstart', onDown, true);
      document.removeEventListener('keydown', onKey, true);
      window.removeEventListener('resize', close);
      window.removeEventListener('scroll', close, true);
    };
  }, [open]);

  const onButtonKey = (e) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const idx = Math.max(0, opts.findIndex((o) => o.value === value));
      const next = opts[(idx + (e.key === 'ArrowDown' ? 1 : opts.length - 1)) % opts.length];
      if (next) choose(next.value);
    }
  };

  const list = open && rect ? ReactDOM.createPortal(
    <div
      ref={listRef}
      role="listbox"
      style={{
        position: 'fixed',
        left: rect.left,
        top: rect.bottom + 4,
        minWidth: rect.width,
        zIndex: 30000,
        background: '#fff',
        border: '1.5px solid #00A19D',
        borderRadius: 8,
        boxShadow: '0 10px 28px rgba(0,161,157,0.22)',
        overflow: 'hidden',
        fontFamily: 'inherit',
      }}
    >
      {opts.map((o) => {
        const selected = o.value === value;
        return (
          <div
            key={String(o.value)}
            role="option"
            aria-selected={selected}
            onClick={() => choose(o.value)}
            onMouseEnter={(e) => { if (!selected) e.currentTarget.style.background = '#E6F6F5'; }}
            onMouseLeave={(e) => { if (!selected) e.currentTarget.style.background = '#fff'; }}
            style={{
              padding: '9px 12px',
              fontSize: 13,
              cursor: 'pointer',
              background: selected ? '#00A19D' : '#fff',
              color: selected ? '#fff' : '#0f172a',
              fontWeight: selected ? 600 : 500,
              whiteSpace: 'nowrap',
            }}
          >
            {o.label}
          </div>
        );
      })}
    </div>,
    document.body
  ) : null;

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        disabled={disabled}
        onClick={() => (open ? setOpen(false) : openList())}
        onKeyDown={onButtonKey}
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
          textAlign: 'left', cursor: disabled ? 'not-allowed' : 'pointer',
          fontFamily: 'inherit', outline: 'none',
          ...style,
          borderColor: open ? '#00A19D' : style.borderColor,
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{current ? current.label : placeholder}</span>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#00A19D" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {list}
    </>
  );
}
